import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useBrand } from '../../context/BrandContext';
import { useCopy } from '../../context/CopyContext';
import { Camera, ScanSearch } from 'lucide-react';
import Header from '../Header';
import Heading from '../Heading';
import Button from '../Button';
import PoweredBy from '../PoweredBy';
import ItemsModalView from './ItemsModalView';

const WelcomeView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);

  const handleContinue = () => {
    dispatch({ type: 'SET_STEP', payload: 2 });
    navigate('/before-start');
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  const handleDebugCamera = () => {
    navigate('/debug-camera');
  };

  const handleDebugDetection = () => {
    navigate('/multi-object-detection');
  };

  return (
    <div className="bg-brand-background min-h-screen flex flex-col relative overflow-hidden" data-node-id="44:219">
      {/* Background Video */}
      <video 
        key={brandConfig.video.background.src}
        className="absolute inset-0 w-full h-full object-cover z-0"
        autoPlay={brandConfig.video.background.autoplay}
        muted={brandConfig.video.background.muted}
        loop={brandConfig.video.background.loop}
        playsInline={brandConfig.video.background.playsInline}
      >
        <source src={brandConfig.video.background.src} type={brandConfig.video.background.type} />
      </video>

      {/* Container with flexbox structure - no padding here */}
      <div className="flex-1 flex flex-col relative">

        {/* Header Section - with padding and high z-index to appear above warning */}
        <div className="flex justify-center mx-20 mt-20 fixed top-0 left-0 right-0 z-[10002]">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={state.itemsCount > 0}
              itemsNumber={state.itemsCount.toString()}
              onItemsClick={handleItemsClick}
              logoColorOverride="dark"
            />
          </div>
        </div>

        {/* Main Content Area - Centered vertically */}
        <motion.div
          className="flex-1 flex items-center justify-center mx-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="text-center w-full max-w-[920px]">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.6,
                ease: [0.25, 0.1, 0.25, 1]
              }}
            >
              <Heading
                title={getCopy('welcome.heading')}
                className="[&>div]:!text-white"
              />
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom Section - with padding */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10">
          {/* Button */}
          <div className="flex justify-center">
            <Button
              className="bg-brand-background [&_p]:!text-brand-text-tertiary"
              variant="primary"
              onClick={handleContinue}
            >
              {getCopy('welcome.buttons.primary')}
            </Button>
          </div>

          {/* Footer */}
          <div className="flex justify-center">
            <PoweredBy
              textColorOverride="white"
              logoColorOverride="dark"
              isWelcomeView={true}
            />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={handleCloseItemsModal}
      />

      {/* Debug Buttons */}
      <div className="fixed bottom-4 right-4 z-[10003] flex flex-col gap-2">
        <button
          onClick={handleDebugDetection}
          className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 opacity-50 hover:opacity-100"
          aria-label="Debug Detection"
          title="Debug Detection View"
        >
          <ScanSearch size={24} />
        </button>
        <button
          onClick={handleDebugCamera}
          className="bg-gray-800 hover:bg-gray-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 opacity-50 hover:opacity-100"
          aria-label="Debug Camera"
          title="Debug Camera"
        >
          <Camera size={24} />
        </button>
      </div>
    </div>
  );
};

export default WelcomeView;
